"use client";

import React, { useCallback, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ProgressBar } from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import SpeechRecognition, { useSpeechRecognition } from "react-speech-recognition";
import { useTranslation } from "react-i18next";
import { useAuth } from "@webuild-ai/react-auth";
import { useBrand, useConfirmationDialog } from "@webuild-ai/react-ui/hooks";
import { Link } from "react-router-dom";
import { Tooltip, TooltipTrigger, TooltipContent } from "@heroui/react";
import { ConfirmationDialog } from "@webuild-ai/react-ui/components";

import { detectMentionTrigger, replaceMention, StructuredMention } from "@/utils/mentionParser";
import { IdentitySearchResult, useMentionStore } from "@/stores/MentionStore";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useToolPreferencesStore } from "@/stores/ToolPreferencesStore";
import { useChatStore, UploadedFile } from "@/stores/ChatStore";
import { isSpecializedWorkflow, getWorkflowDefaultPrompt } from "@/utils/workflowUtils";
import { useTheme } from "@/hooks/use-theme";
import { usePromptAccent } from "@/hooks/usePromptAccent";
import { blurActiveElement } from "@/utils/dom";
import { useFileUploadError } from "@/hooks/useFileUploadError";
import { FileUploadError } from "@/components/FileUpload/FileUploadError";
import { createDefaultChatWorkflow } from "@/utils/chat/workflowTools";
import { createStructuredUploadError } from "@/utils/chat/validationHelpers";
import {
  ALLOWED_FILE_TYPES,
  UPLOAD_POLL_INTERVAL_MS,
  fileUploadStatusLabel,
} from "@/utils/chat/fileOperations";
import { useFileUpload } from "@/hooks/chat/useFileUpload";
import { useWorkflowList } from "@/hooks/chat/useWorkflowList";
import { useSkillList } from "@/hooks/chat/useSkillList";
import { usePasteHandler } from "@/hooks/chat/usePasteHandler";
import { convertFileTypeToIcon } from "@/utils/ConvertFileTypeToIcon";
import { FileTypeIcon } from "@/components/UI/FileTypeIcon";
import { logger } from "@/telemetry";
import { useUIPreferences } from "@/stores/UIPreferencesStore";

import { MentionChip } from "./MentionChip";
import { MentionAutocomplete } from "./MentionAutocomplete";
import { ChatSettingsModal } from "./ChatSettingsModal";
import { PromptSearchModal } from "./PromptSearchModal";
import { FeatureCardProps } from "./FeatureCards";
import { FeatureCards } from "./FeatureCards";
import { RichPromptInput } from "./RichPromptInput";
import { PromptSuggestions, PromptSuggestion } from "./PromptSuggestions";
import { ChatInputActions } from "./ChatInputActions";

interface PromptWithActionsProps {
  submit: (prompt: string, files: UploadedFile[], mentions?: StructuredMention[]) => Promise<void>;
  streaming: boolean;
  suggestions: PromptSuggestion[];
  features: FeatureCardProps[];
  showFileUpload?: boolean;
  showVoice?: boolean;
  showPrompts?: boolean;
  showPrompto?: boolean;
  showSearch?: boolean;
  showKnowledgeHub?: boolean;
  showSettings?: boolean;
  showChatModelSelector?: boolean;
  messagesCount?: number;
  expandToFill?: boolean;
}

/** Delay (ms) after voice recognition stops before the transcript is cleared. */
const VOICE_TRANSCRIPT_SETTLE_MS = 100;

export const PromptWithActions = ({
  submit,
  streaming,
  suggestions,
  features,
  showFileUpload,
  showVoice,
  showPrompts,
  showPrompto,
  showSearch,
  showKnowledgeHub,
  showSettings,
  showChatModelSelector,
  messagesCount = 0,
  expandToFill = false,
}: PromptWithActionsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [prompt, setPrompt] = React.useState<string>("");
  // OCR mode is not toggled in the UI currently; kept for the upload callback
  const isOCRMode = false;
  const { errorState, clearError, retryUpload, setUploadError } = useFileUploadError();
  const [initialPrompt, setInitialPrompt] = React.useState<string>("");

  // Command menu state (for "/" affordance)
  const [commandMenuVisible, setCommandMenuVisible] = React.useState(false);
  const [commandMenuPosition, setCommandMenuPosition] = React.useState({ top: 0, left: 0 });

  // Bumped to open the "+" add menu via keyboard (user typed "+" in an empty composer).
  const [openAddMenuSignal, setOpenAddMenuSignal] = React.useState(0);

  // Mention autocomplete state
  const [mentionState, setMentionState] = React.useState<{
    visible: boolean;
    query: string;
    startIndex: number;
    position: { top: number; left: number };
  }>({
    visible: false,
    query: "",
    startIndex: 0,
    position: { top: 0, left: 0 },
  });

  // Track selected mentions with their identity data for payload & display
  const [selectedMentions, setSelectedMentions] = React.useState<Map<string, IdentitySearchResult>>(
    new Map(),
  );
  const { addRecentIdentity } = useMentionStore();
  const promptInputRef = useRef<HTMLTextAreaElement>(null);

  const { platformClient, adminClient } = useWebuildAPIClient();
  const { setWorkflow } = useWorkflowStore();
  const workflow = useWorkflowStore((s) => s.workflow);
  const { runId, stopStreaming, resetPrompt, clearResetPrompt, chat } = useChatStore();
  const { brand } = useBrand();
  // Workflows surfaced in the @ popup (its own list, with w1..wN shortcuts).
  const { workflows: mentionWorkflows } = useWorkflowList();
  // Skills surfaced in the @ popup (its own list, with s1..sN shortcuts).
  const { skills: mentionSkills, refreshIfStale: refreshSkillsIfStale } = useSkillList();

  // The composer stays mounted across "new chat", so the skill list fetched at
  // mount goes stale when a skill is created elsewhere (Settings invalidates
  // the shared cache directly; the skill-creator agent in a chat cannot).
  // Refetch a stale list whenever the user opens a menu that offers skills.
  React.useEffect(() => {
    if (mentionState.visible || commandMenuVisible) {
      refreshSkillsIfStale();
    }
  }, [mentionState.visible, commandMenuVisible, refreshSkillsIfStale]);
  const { isDark } = useTheme();
  const promptAccentStyle = usePromptAccent();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();

  // UI preferences for simple/advanced mode and pinned controls
  const {
    simpleChatInput,
    pinnedChatControls,
    webSearchDefault,
    knowledgeHubDefault,
    setWebSearchDefault,
    setKnowledgeHubDefault,
    simpleChatInputHintDismissed,
    dismissSimpleChatInputHint,
  } = useUIPreferences();

  // Determine Configuration button disabled state (same logic as ChatModelSelector)
  const hasMessages = chat?.messages && chat?.messages.length > 0;
  const isSpecialized = isSpecializedWorkflow(workflow);
  const isConfigurationDisabled = isSpecialized || hasMessages;

  // Check if workflow has a pre-filled default prompt value
  const workflowDefaultPrompt = getWorkflowDefaultPrompt(workflow);
  const isPromptLocked = !!workflowDefaultPrompt;

  const {
    files: uploadedFiles,
    uploadProgress,
    isUploading,
    failedFiles,
    fileInputRef,
    error: uploadError,
    handleFileChange,
    handleDrop: handleFileDrop,
    removeFile,
    retryFailedUploads,
    clearFiles,
  } = useFileUpload({
    onUpload: useCallback(
      async (files: File[]) => {
        let chatId = runId;

        if (!chatId && platformClient && user) {
          const { create } = useChatStore.getState();
          const userId = user.id;
          const userTenant = user.tenant || "default";

          // Get the current workflow or use a default chat workflow
          const currentWorkflow = workflow || createDefaultChatWorkflow("gpt-4.1");

          chatId = await create(platformClient, currentWorkflow, userId, userTenant, "File Upload");
        }

        if (!chatId) {
          throw new Error("Failed to create or retrieve chat ID");
        }

        const uploadedFiles: UploadedFile[] = await Promise.all(
          files.map(async (file) => {
            const response = await platformClient?.files.upload({
              bodyUploadFileBackgroundPlatformV1DataFilesPost: {
                file: file,
                summary: file.name,
                tags: [],
                fileType: file.type,
                chatId: chatId,
                private: true,
                parser: isOCRMode ? "ocr" : "llm",
              },
            });

            if (!response) {
              throw new Error(`Upload failed for ${file.name}`);
            }

            // Poll job status until processing completes
            let jobStatus = await platformClient?.files.getJobStatus({
              jobId: response.job_id,
            });

            while (jobStatus?.status === "pending") {
              await new Promise((resolve) => setTimeout(resolve, UPLOAD_POLL_INTERVAL_MS));
              jobStatus = await platformClient?.files.getJobStatus({
                jobId: response.job_id,
              });
            }

            // Handle failed uploads (e.g., quarantined files)
            if (jobStatus?.status === "failed") {
              const errorMessage =
                jobStatus.result ||
                t("fileUpload.errors.generic.message", { defaultValue: "File upload failed" });

              // Attach the full job status as structured error data for proper error parsing
              const error = createStructuredUploadError(errorMessage, jobStatus);

              throw error;
            }

            return {
              name: file.name,
              size: String(file.size),
              type: file.type,
              id: jobStatus?.result,
              status: "completed" as const,
            };
          }),
        );

        return uploadedFiles;
      },
      [platformClient, user, runId, workflow, isOCRMode, t],
    ),

    onValidate: (_files: File[]) => {
      // Simple synchronous validation — async validation is handled by onUpload
      return { isValid: true, errors: [] };
    },
    maxFiles: 10,
  });

  // Sync upload error with error state
  React.useEffect(() => {
    if (uploadError && failedFiles.length > 0) {
      setUploadError(uploadError, failedFiles[0], user?.id, user?.tenant);
    } else if (!uploadError && failedFiles.length === 0) {
      // Clear error when upload succeeds or is cleared
      clearError();
    }
  }, [uploadError, failedFiles, setUploadError, clearError, user]);

  const [isSettingsModalOpen, setIsSettingsModalOpen] = React.useState(false);
  const [isSearchEnabled, setIsSearchEnabled] = React.useState(false);
  const [isKnowledgeHubEnabled, setIsKnowledgeHubEnabled] = React.useState(false);
  const {
    isOpen: isConfirmDialogOpen,
    showConfirmation,
    confirmationProps,
  } = useConfirmationDialog();
  // voice
  const { transcript, listening, browserSupportsSpeechRecognition, resetTranscript } =
    useSpeechRecognition();

  const [isTemplateModalOpen, setIsTemplateModalOpen] = React.useState(false);
  const [isDragging, setIsDragging] = React.useState(false);
  const [speechError, setSpeechError] = React.useState<string | null>(null);

  const listeningRef = useRef(false);

  // Check if we're currently on the Prompto workflow page
  const isOnPromptoWorkflow = location.pathname === "/workflows/prompt_generator_workflow";

  React.useEffect(() => {
    logger.info("workflow", { workflowId: workflow?.id, workflowName: workflow?.name });
  }, [workflow?.id, workflow?.name]);

  // Check for secure context
  React.useEffect(() => {
    if (window.location.protocol !== "https:" && window.location.hostname !== "localhost") {
      setSpeechError(t("Speech recognition requires HTTPS in production"));
    }
  }, [t]);

  // Clear prompt and uploaded files when resetPrompt is triggered
  React.useEffect(() => {
    if (resetPrompt) {
      logger.info("resetPrompt", { resetPrompt: String(resetPrompt) });
      setPrompt("");
      clearFiles();
      clearResetPrompt();
    }
  }, [resetPrompt, clearResetPrompt, clearFiles]);

  // Pre-fill prompt from workflow default value when workflow changes
  React.useEffect(() => {
    const defaultPrompt = getWorkflowDefaultPrompt(workflow);

    if (defaultPrompt) {
      setPrompt(defaultPrompt);
    }
  }, [workflow, setPrompt]);

  // Mention detection: Check for @ trigger when prompt changes
  const handlePromptChange = useCallback(
    (value: string) => {
      // "+" typed into an empty composer opens the add menu — the keyboard
      // equivalent of clicking the + button (mirrors "/" for commands and "@"
      // for mentions). The "+" itself is not kept in the input.
      if (value === "+") {
        setOpenAddMenuSignal((n) => n + 1);
        setCommandMenuVisible(false);
        setMentionState((prev) => ({ ...prev, visible: false }));

        return;
      }

      setPrompt(value);

      // Sync selectedMentions: remove any mention whose @DisplayName is no longer in the text
      setSelectedMentions((prev) => {
        let changed = false;
        const next = new Map(prev);

        for (const [key, identity] of prev) {
          const pattern = new RegExp(
            `@${identity.displayName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`,
            "i",
          );

          if (!pattern.test(value)) {
            next.delete(key);
            changed = true;
          }
        }

        return changed ? next : prev;
      });

      // Detect mention trigger
      const textarea = promptInputRef.current;

      if (!textarea) {
        setMentionState((prev) => ({ ...prev, visible: false }));

        return;
      }

      const cursorPosition = textarea.selectionStart || value.length;
      const trigger = detectMentionTrigger(value, cursorPosition);

      if (trigger && trigger.active) {
        // Calculate viewport position of the cursor line
        const rect = textarea.getBoundingClientRect();
        const lineHeight = promptInputRef.current
          ? parseFloat(window.getComputedStyle(promptInputRef.current).lineHeight) || 24
          : 24;
        const lines = value.substring(0, cursorPosition).split("\n").length;
        const cursorOffsetInTextarea = (lines - 1) * lineHeight - textarea.scrollTop;

        setMentionState({
          visible: true,
          query: trigger.query,
          startIndex: trigger.startIndex,
          position: {
            top: rect.top + cursorOffsetInTextarea,
            left: rect.left + 16,
          },
        });
      } else {
        setMentionState((prev) => ({ ...prev, visible: false }));
      }

      // Detect "/" at the start of input to open / keep open the command menu.
      // The menu stays open while the prompt starts with "/" so the user can
      // type a query (e.g. "/prop") to filter skill rows.
      if (value.trimStart().startsWith("/")) {
        const rect = textarea.getBoundingClientRect();

        setCommandMenuPosition({ top: rect.top, left: rect.left + 16 });
        setCommandMenuVisible(true);
      } else {
        setCommandMenuVisible(false);
      }
    },
    [setPrompt],
  );

  // Handle mention selection from autocomplete
  const handleMentionSelect = useCallback(
    (identity: IdentitySearchResult) => {
      const textarea = promptInputRef.current;
      const cursorPosition = textarea?.selectionStart || prompt.length;

      const { text, newCursorPosition } = replaceMention(
        prompt,
        mentionState.startIndex,
        cursorPosition,
        `@${identity.displayName}`,
      );

      setPrompt(text);
      setMentionState((prev) => ({ ...prev, visible: false }));

      // Store selected mention for payload & visual display
      setSelectedMentions((prev) => {
        const newMap = new Map(prev);

        newMap.set(identity.displayName.toLowerCase(), identity);

        return newMap;
      });

      // Add to recent identities for quick access
      addRecentIdentity(identity);

      // Restore focus and cursor position
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);
    },
    [prompt, mentionState.startIndex, addRecentIdentity],
  );

  // Close mention autocomplete
  const handleMentionClose = useCallback(() => {
    setMentionState((prev) => ({ ...prev, visible: false }));
  }, []);

  // Switch the chat to a workflow chosen from the @ popup. Same mechanism as the
  // command-menu picker: navigate to /workflows/:id (Prompto's path), which
  // starts a fresh chat with that workflow.
  const handleMentionWorkflowSelect = useCallback(
    (workflowId: string) => {
      logger.info("PromptWithActions: workflow selected from @ menu", { workflowId });
      setMentionState((prev) => ({ ...prev, visible: false }));
      navigate(`/workflows/${workflowId}`);
    },
    [navigate],
  );

  // Insert a skill reference token chosen from the @ popup. Unlike workflows
  // (which navigate), skills are INSERTED as an `@s_<id>` token. We replace the
  // active `@s…` trigger text in place using the same replaceMention helper the
  // people path uses, so "@s" / "@s2" becomes "@s_<id> " with a trailing space.
  const handleMentionSkillSelect = useCallback(
    (skillId: string) => {
      logger.info("PromptWithActions: skill selected from @ menu", { skillId });

      const textarea = promptInputRef.current;
      const cursorPosition = textarea?.selectionStart || prompt.length;

      const { text, newCursorPosition } = replaceMention(
        prompt,
        mentionState.startIndex,
        cursorPosition,
        `@s_${skillId}`,
      );

      setPrompt(text);
      setMentionState((prev) => ({ ...prev, visible: false }));

      // Restore focus and place the caret after the inserted token + space.
      setTimeout(() => {
        if (textarea) {
          textarea.focus();
          textarea.setSelectionRange(newCursorPosition, newCursorPosition);
        }
      }, 0);
    },
    [prompt, mentionState.startIndex],
  );

  /**
   * Triggered by the "Mention people or workflows" item in the add menu.
   * Inserts "@" at the caret position, opens the MentionAutocomplete with an
   * empty query (showing recent mentions), and moves the caret to just after
   * the inserted "@" so that the user can immediately start typing to filter.
   */
  const handleMention = useCallback(() => {
    const textarea = promptInputRef.current;
    const cursor = textarea?.selectionStart ?? prompt.length;
    const newValue = prompt.slice(0, cursor) + "@" + prompt.slice(cursor);

    setPrompt(newValue);

    if (textarea) {
      const rect = textarea.getBoundingClientRect();
      const lineHeight = parseFloat(window.getComputedStyle(textarea).lineHeight) || 24;
      const lines = newValue.substring(0, cursor).split("\n").length;
      const cursorOffsetInTextarea = (lines - 1) * lineHeight - textarea.scrollTop;

      setMentionState({
        visible: true,
        query: "",
        startIndex: cursor,
        position: {
          top: rect.top + cursorOffsetInTextarea,
          left: rect.left + 16,
        },
      });
    }

    requestAnimationFrame(() => {
      const ta = promptInputRef.current;

      if (ta) {
        ta.focus();
        const pos = cursor + 1;

        ta.setSelectionRange(pos, pos);
      }
    });
  }, [prompt]);

  /**
   * Insert a skill reference token at the caret, mirroring how a mention is
   * inserted (see handleMention). Skills are referenced as `@s_<id>` — the same
   * `@`-prefixed token convention the chat uses for people and (conceptually)
   * workflows, with an `s_` namespace so the backend can resolve it to a skill.
   * No sample prompt is inserted; the user types their request after the token.
   */
  const handleSelectSkill = useCallback(
    (skillId: string) => {
      const token = `@s_${skillId}`;
      const textarea = promptInputRef.current;
      const cursor = textarea?.selectionStart ?? prompt.length;
      // When the menu was opened by typing "/", the prompt is just the "/" query
      // (e.g. "/doc"). Replace it wholesale so we don't leave a stray slash.
      const startsWithSlash = prompt.trimStart().startsWith("/");
      const newValue = startsWithSlash
        ? `${token} `
        : `${prompt.slice(0, cursor)}${token} ${prompt.slice(cursor)}`;

      setPrompt(newValue);
      setCommandMenuVisible(false);

      const caret = startsWithSlash ? newValue.length : cursor + token.length + 1;

      requestAnimationFrame(() => {
        const ta = promptInputRef.current;

        if (ta) {
          ta.focus();
          ta.setSelectionRange(caret, caret);
        }
      });
    },
    [prompt],
  );

  // Remove a selected mention (when clicking X on chip)
  const handleRemoveMention = useCallback((displayName: string) => {
    // Remove from selected mentions
    setSelectedMentions((prev) => {
      const newMap = new Map(prev);

      newMap.delete(displayName.toLowerCase());

      return newMap;
    });

    // Remove from prompt text
    const mentionPattern = new RegExp(
      `@${displayName.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s?`,
      "gi",
    );

    setPrompt((prev) => prev.replace(mentionPattern, "").trim());
  }, []);

  const handleSubmit = async (promptText: string) => {
    const fileToUpload = uploadedFiles;

    // Build structured mentions from selected identities (autocomplete selections).
    const mentions: StructuredMention[] = Array.from(selectedMentions.values()).map((identity) => ({
      identity_id: identity.id,
      display_text: `@${identity.displayName}`,
      resolved_name: identity.displayName,
      canonical_email: identity.canonicalEmail,
      target_mode: "all" as const,
      service_user_ids: identity.serviceUserIds,
    }));

    setPrompt(""); // Clear the prompt after submission
    setInitialPrompt(""); // Clear voice input initial prompt to prevent stale state
    resetTranscript(); // Clear any lingering voice transcript
    clearFiles();
    setSelectedMentions(new Map()); // Clear selected mentions after submit
    await submit(promptText, fileToUpload, mentions.length > 0 ? mentions : undefined);
  };

  const handleSubmitEvent = async (event: React.FormEvent | React.MouseEvent) => {
    event.preventDefault();
    // Guard against an accidental double-send: while a response is still
    // streaming, pressing Enter (or the form submit) must not fire a second
    // workflow invocation on the same workflow. The send button becomes a stop
    // button while streaming, so the only remaining submit path is the keyboard
    // Enter key, which we swallow here (PWI-145). Typing and Shift+Enter newlines
    // are unaffected because they never reach this handler.
    if (streaming) {
      return;
    }
    if (!prompt) {
      return;
    }
    await handleSubmit(prompt);
  };

  const setWorkflowTool = useCallback(
    (toolOrTools: string | string[], isSelected: boolean) => {
      if (!workflow?.agents) return;

      const chatAgentEntry = Object.entries(workflow.agents).find(
        ([_, agent]) => agent.type === "chat_agent",
      );

      if (!chatAgentEntry) return;

      const [agentId, agent] = chatAgentEntry;
      const currentTools: string[] = agent?.parameters?.tools || [];
      const toolsToModify = Array.isArray(toolOrTools) ? toolOrTools : [toolOrTools];

      let updatedTools: string[];

      if (isSelected) {
        // Add tools if not already present
        updatedTools = [
          ...currentTools,
          ...toolsToModify.filter((tool) => !currentTools.includes(tool)),
        ];
      } else {
        // Remove tools if present
        updatedTools = currentTools.filter((tool) => !toolsToModify.includes(tool));
      }

      const updatedAgent = {
        ...agent,
        parameters: {
          ...agent.parameters,
          tools: updatedTools,
        },
      };

      const updatedWorkflow = {
        ...workflow,
        agents: {
          ...workflow.agents,
          [agentId]: updatedAgent,
        },
      };

      setWorkflow(updatedWorkflow);
    },
    [workflow, setWorkflow],
  );

  const handleWebSearchToggle = async (val: boolean) => {
    // If there's an active chat, show confirmation dialog
    if (runId) {
      // Translate the verb so non-English locales don't show the English word
      // mid-sentence (e.g. "Um die Web-Suche zu enable…"). The message keeps the
      // {{action}} placeholder; only the interpolated value is localised.
      const action = t(val ? "websearch-action-enable" : "websearch-action-disable");
      const confirmed = await showConfirmation({
        title: t("websearch-change-setting-title"),
        message: t("websearch-confirmation-message", { action }),
        confirmText: t("Continue"),
        cancelText: t("Cancel"),
        confirmColor: "primary",
        icon: <Icon icon="solar:settings-bold" width={24} />,
      });

      if (!confirmed) {
        // User cancelled, don't change anything
        return;
      }

      // User confirmed, update tools and start new chat
      setWorkflowTool(["serpapi_search", "crawler"], val);
      setIsSearchEnabled(val);

      // Start new chat to apply changes since backend locks in tools on session creation
      const { newChat } = useChatStore.getState();

      newChat();
    } else {
      // No active chat, just update the state
      setIsSearchEnabled(val);
      setWorkflowTool(["serpapi_search", "crawler"], val);
    }

    // Persist via the store action so subscribers (e.g. ChatInputSettings) stay in sync.
    setWebSearchDefault(val);
  };

  const handleKnowledgeHub = () => {
    const next = !isKnowledgeHubEnabled;

    // setWorkflowTool(next) keeps the tool state aligned with the UI toggle (idempotent).
    setWorkflowTool("knowledge_base_retriever", next);
    setIsKnowledgeHubEnabled(next);
    // Persist the choice so it survives a refresh (mirrors Web Search persistence)
    setKnowledgeHubDefault(next);
    // Remove focus to prevent Enter key from toggling again
    setTimeout(() => {
      blurActiveElement();
    }, 0);
  };

  const handleTemplateSelect = (template: string) => {
    setPrompt(template);
    setIsTemplateModalOpen(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleVoicePress = useCallback(async () => {
    if (!browserSupportsSpeechRecognition) {
      setSpeechError(t("Your browser doesn't support speech recognition"));

      return;
    }
    if (listeningRef.current) {
      // STOP
      try {
        SpeechRecognition.stopListening();
        listeningRef.current = false;
        setSpeechError(null);
      } catch {
        setSpeechError(t("Failed to stop speech recognition"));
        try {
          SpeechRecognition.abortListening();
        } catch {
          // Silently ignore abort errors
        }
      }
    } else {
      setInitialPrompt(prompt);
      try {
        await SpeechRecognition.startListening({
          continuous: true,
          interimResults: true,
          language: i18n.language,
        });
        listeningRef.current = true;
        setSpeechError(null);
      } catch {
        setSpeechError(t("Failed to start speech recognition"));
        if (window.location.protocol !== "https:") {
          setSpeechError(t("Speech recognition requires HTTPS in production"));
        }
      }
    }
    setTimeout(() => {
      blurActiveElement();
    }, 0);
  }, [browserSupportsSpeechRecognition, prompt, i18n.language, t]);

  // Track if we've ever been listening (to avoid resetting on initial render)
  const hasListenedRef = useRef(false);

  // Sync listeningRef with actual listening state from the hook
  useEffect(() => {
    listeningRef.current = listening;
    if (listening) {
      hasListenedRef.current = true;
    }
  }, [listening]);

  // Update prompt with transcript when actively listening
  useEffect(() => {
    if (listening && transcript) {
      setPrompt(initialPrompt + (initialPrompt ? " " : "") + transcript);
    }
  }, [transcript, initialPrompt, listening]);

  // Reset transcript when listening stops (only after we've actually listened)
  useEffect(() => {
    if (!listening && hasListenedRef.current) {
      // delay a tick to let last transcript event settle, then clear
      const id = setTimeout(() => {
        resetTranscript();
        hasListenedRef.current = false;
      }, VOICE_TRANSCRIPT_SETTLE_MS);

      return () => clearTimeout(id);
    }
  }, [listening, resetTranscript]);

  useEffect(() => {
    return () => {
      try {
        SpeechRecognition.stopListening();
        resetTranscript();
        listeningRef.current = false;
      } catch {
        // Silently ignore stop listening errors
      }
    };
  }, [resetTranscript]);

  // Initialise web search from UIPreferences store (replaces ad-hoc localStorage reads)
  const webSearchInitRef = useRef(false);

  useEffect(() => {
    if (!workflow || webSearchInitRef.current) return;

    webSearchInitRef.current = true;

    // Apply the store default to the workflow tools
    setIsSearchEnabled(webSearchDefault);
    setWorkflowTool(["serpapi_search", "crawler"], webSearchDefault);
  }, [workflow, webSearchDefault, setWorkflowTool]);

  // Sync search state from workflow tool changes (e.g., after new chat)
  useEffect(() => {
    if (workflow?.agents) {
      const chatAgent = Object.values(workflow.agents).find((agent) => agent.type === "chat_agent");

      if (chatAgent) {
        const tools: string[] = chatAgent?.parameters?.tools || [];
        const enabled = tools.includes("serpapi_search") || tools.includes("crawler");

        setIsSearchEnabled(enabled);
      }
    }
  }, [workflow]);

  // Initialise user memory from localStorage (default: enabled) — run once.
  const memoryInitRef = useRef(false);

  useEffect(() => {
    if (!workflow || memoryInitRef.current) return;

    // If ToolPreferencesStore already has preferences for this workflow,
    // they were applied during loadWorkflow — skip legacy init
    if (useToolPreferencesStore.getState().hasPreferences(workflow.id)) {
      memoryInitRef.current = true;

      return;
    }

    const chatAgentEntry = Object.entries(workflow.agents || {}).find(
      ([, agent]) => agent.type === "chat_agent",
    );

    if (!chatAgentEntry) return;

    memoryInitRef.current = true;

    const [agentId, chatAgent] = chatAgentEntry;
    const savedPreference = localStorage.getItem("userMemoryEnabled");

    let enabled = true;

    if (savedPreference !== null) {
      try {
        enabled = JSON.parse(savedPreference) as boolean;
      } catch {
        // Corrupted localStorage value — default to enabled
      }
    }
    const tools = (chatAgent?.parameters?.tools as string[] | undefined) || [];
    const alreadySet = tools.includes("save_to_memory");

    if (enabled !== alreadySet) {
      setWorkflowTool("save_to_memory", enabled);
    }

    // Migrate legacy preference into ToolPreferencesStore and remove the old key
    const finalTools = enabled
      ? alreadySet
        ? [...tools]
        : [...tools, "save_to_memory"]
      : tools.filter((t: string) => t !== "save_to_memory");

    useToolPreferencesStore.getState().setAgentTools(workflow.id, agentId, finalTools);

    if (savedPreference !== null) {
      localStorage.removeItem("userMemoryEnabled");
    }
  }, [workflow, setWorkflowTool]);

  // Initialise Knowledge Hub from UIPreferences store
  useEffect(() => {
    setIsKnowledgeHubEnabled(knowledgeHubDefault);
    if (workflow) {
      // Use setWorkflowTool (idempotent) rather than toggleWorkflowTool so the tool is
      // set to the desired state regardless of whether the workflow config already has it.
      setWorkflowTool("knowledge_base_retriever", knowledgeHubDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: only on mount
  }, []);

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;

    if (files && files.length > 0) {
      // Use the hook's handleFileChange method
      const fileList = new DataTransfer();

      Array.from(files).forEach((file) => fileList.items.add(file));

      if (fileInputRef.current) {
        fileInputRef.current.files = fileList.files;
        await handleFileChange({
          target: fileInputRef.current,
        } as React.ChangeEvent<HTMLInputElement>);
      }
    }
  };

  const handlePaste = usePasteHandler(handleFileDrop);

  const handleOpenCommandMenu = useCallback(() => {
    const textarea = promptInputRef.current;

    if (textarea) {
      const rect = textarea.getBoundingClientRect();

      setCommandMenuPosition({ top: rect.top, left: rect.left + 16 });
    }
    setCommandMenuVisible(true);
  }, []);

  const handleCloseCommandMenu = useCallback(() => {
    setCommandMenuVisible(false);
    // Clear a lone leading "/" when the menu is dismissed without a selection.
    // Use a functional update so a token inserted by a selection handler in the
    // same event (e.g. a skill's `@s_<id>`) survives: the menu calls onSelect()
    // then onClose() synchronously, and a stale `prompt` closure here would
    // otherwise clobber the just-inserted token back to "".
    setPrompt((current) => (current.trim() === "/" ? "" : current));
  }, []);

  const isUploadingFiles = uploadedFiles.some(
    (f) => f.status === "uploading" || f.status === "processing",
  );

  return (
    <div className={cn("flex flex-col", expandToFill && "h-full min-h-0")}>
      {features.length > 0 && (
        <div className="flex w-full flex-col gap-4" data-id="feature-cards-container">
          <FeatureCards cards={features} onSelectFn={(p: string) => handleSubmit(p)} />
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="flex w-full flex-col gap-4 pl-4" data-id="prompt-suggestions-container">
          <PromptSuggestions suggestions={suggestions} onSelect={setPrompt} />
        </div>
      )}

      {/* One-time simplified-input hint */}
      {simpleChatInput && !simpleChatInputHintDismissed && (
        <div
          className="mx-4 mb-2 flex items-center gap-2 rounded-lg bg-default-100 px-3 py-2 text-xs text-default-600"
          data-id="simple-chat-input-hint"
          role="status"
        >
          <Icon className="shrink-0 text-default-400" icon="solar:info-circle-linear" width={14} />
          <span>
            {t("chatInput.simpleModeHint")}{" "}
            <Link className="text-primary underline" to="/settings#general">
              {t("chatInput.simpleModeHintLink")}
            </Link>
          </span>
          <button
            aria-label={t("chatInput.simpleModeHintDismiss")}
            className="ml-auto shrink-0 rounded p-0.5 text-default-400 hover:text-default-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            type="button"
            onClick={dismissSimpleChatInputHint}
          >
            <Icon icon="solar:close-circle-linear" width={14} />
          </button>
        </div>
      )}

      <div
        className={cn("flex w-full flex-col gap-4 px-4 pb-4", expandToFill && "flex-1 min-h-0")}
        data-id="prompt-form-container"
      >
        {workflow?.name &&
          workflow.name !== "Chat Agent" &&
          workflow.name !== "Prompt Generator Workflow" && (
            <div className="w-full -mb-3 px-2">
              <div
                className="w-full px-3 pt-1.5 pb-3 text-tiny font-medium h-auto min-h-0 rounded-t-xl select-none cursor-default transition-colors flex items-center gap-1.5"
                data-id="workflow-label"
                style={{
                  backgroundColor: `color-mix(in srgb, ${
                    isDark ? brand.colours.dark.primary : brand.colours.light.primary
                  } ${isDark ? 22 : 12}%, transparent)`,
                  color: isDark ? brand.colours.dark.primary : brand.colours.light.primary,
                }}
              >
                <Icon className="text-base" icon="gravity-ui:face-robot" />
                <span>{t(`workflow.${workflow.name}`, { defaultValue: workflow.name })}</span>
              </div>
            </div>
          )}
        <form
          className={cn(
            "relative flex w-full flex-col items-start rounded-2xl overflow-hidden transition-[border-color,box-shadow] duration-200",
            "bg-default-50 dark:bg-content2",
            "border border-(--prompt-accent-border)",
            "shadow-[0_1px_2px_rgba(0,0,0,0.03),0_6px_20px_-8px_rgba(0,0,0,0.06)]",
            "dark:shadow-[0_1px_2px_rgba(0,0,0,0.25),0_8px_24px_-10px_rgba(0,0,0,0.35)]",
            "hover:border-(--prompt-accent-hover) hover:shadow-[0_2px_6px_rgba(0,0,0,0.04),0_10px_28px_-10px_rgba(0,0,0,0.08)]",
            "focus-within:border-(--prompt-accent) focus-within:ring-4 focus-within:ring-(--prompt-accent-ring)",
            isDragging && "border-dashed! border-(--prompt-accent)! bg-(--prompt-accent-ring)",
            expandToFill && "flex-1 min-h-0",
          )}
          data-id="prompt-form"
          style={promptAccentStyle}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onSubmit={handleSubmitEvent}
        >
          {/* Selected Mentions Chips */}
          {selectedMentions.size > 0 && (
            <div className="flex flex-wrap gap-2 px-4 pt-3" data-id="selected-mentions-container">
              {Array.from(selectedMentions.values()).map((identity) => (
                <MentionChip
                  key={identity.id}
                  avatarUrl={identity.avatarUrl}
                  displayName={identity.displayName}
                  services={identity.availableServices}
                  onRemove={() => handleRemoveMention(identity.displayName)}
                />
              ))}
            </div>
          )}

          <RichPromptInput
            ref={promptInputRef}
            classNames={{
              inputWrapper: "bg-transparent! shadow-none",
              innerWrapper: "relative",
              input: "pt-1 pl-2 pb-6 pr-14! text-medium",
            }}
            data-id="prompt-input"
            endContent={
              // Send/stop button inset inside the textarea — kept for visual alignment;
              // ChatInputActions also renders send/stop at the bottom. We keep this one
              // hidden when ChatInputActions is present so keyboard submit still works.
              // The form's submit event handles Enter-key submission.
              undefined
            }
            expandToFill={expandToFill}
            isOverlayOpen={mentionState.visible || commandMenuVisible}
            messagesCount={messagesCount}
            minRows={3}
            selectedMentions={selectedMentions}
            value={prompt}
            onPaste={handlePaste}
            onSubmit={handleSubmitEvent}
            onValueChange={isPromptLocked ? () => {} : handlePromptChange}
          />

          {/* Mention Autocomplete Dropdown */}
          <MentionAutocomplete
            activeWorkflowId={workflow?.id ?? null}
            position={mentionState.position}
            query={mentionState.query}
            skills={mentionSkills}
            skillsLabel={t("chatCommandMenu.sections.skills")}
            visible={mentionState.visible}
            workflows={mentionWorkflows}
            workflowsLabel={brand.copy.menuItems?.["Workflows"]}
            onClose={handleMentionClose}
            onSelect={handleMentionSelect}
            onSelectSkill={handleMentionSkillSelect}
            onSelectWorkflow={handleMentionWorkflowSelect}
          />

          {/* Prompt-locked indicator */}
          {isPromptLocked && (
            <div className="absolute top-3 right-3 z-20">
              <Tooltip>
                <TooltipTrigger>
                  <Icon
                    className="text-default-400"
                    data-id="prompt-locked-icon"
                    icon="solar:lock-linear"
                    width={14}
                  />
                </TooltipTrigger>
                <TooltipContent showArrow>
                  {t("Input is pre-filled and locked for this workflow")}
                </TooltipContent>
              </Tooltip>
            </div>
          )}

          {/* Shared action row */}
          <ChatInputActions
            allowedFileTypes={ALLOWED_FILE_TYPES}
            browserSupportsSpeechRecognition={browserSupportsSpeechRecognition}
            commandMenuPosition={commandMenuPosition}
            commandMenuVisible={commandMenuVisible}
            fileInputRef={fileInputRef as React.RefObject<HTMLInputElement>}
            isConfigurationDisabled={!!isConfigurationDisabled}
            isKnowledgeHubEnabled={isKnowledgeHubEnabled}
            isPromptLocked={isPromptLocked}
            isSearchEnabled={isSearchEnabled}
            isUploading={isUploading}
            isUploadingFiles={isUploadingFiles}
            listening={listening}
            openAddMenuSignal={openAddMenuSignal}
            pinnedChatControls={pinnedChatControls}
            prompt={prompt}
            showChatModelSelector={!!showChatModelSelector}
            showFileUpload={!!showFileUpload}
            showKnowledgeHub={!!showKnowledgeHub}
            showPrompto={!!showPrompto}
            showPrompts={!!showPrompts}
            showSearch={!!showSearch}
            showSettings={!!showSettings}
            showVoice={!!showVoice}
            simpleChatInput={simpleChatInput}
            speechError={speechError}
            streaming={streaming}
            onBrowsePrompts={() => setIsTemplateModalOpen(true)}
            onCommandMenuClose={handleCloseCommandMenu}
            onCommandMenuOpen={handleOpenCommandMenu}
            onFileChange={handleFileChange}
            onFileUploadClick={() => {
              fileInputRef.current?.click();
              setTimeout(() => {
                blurActiveElement();
              }, 0);
            }}
            onKnowledgeHubToggle={handleKnowledgeHub}
            onMention={handleMention}
            onOpenConfiguration={() => {
              if (!isConfigurationDisabled) {
                setIsSettingsModalOpen(true);
                setTimeout(() => {
                  blurActiveElement();
                }, 0);
              }
            }}
            onPrompto={() => navigate("/workflows/prompt_generator_workflow")}
            onSelectSkill={handleSelectSkill}
            onStop={() =>
              adminClient && platformClient && stopStreaming(adminClient, platformClient, true)
            }
            onVoicePress={handleVoicePress}
            onWebSearchToggle={handleWebSearchToggle}
          />

          {/* Upload Progress */}
          {isUploading && (
            <div className="w-full px-4 pb-2" data-id="upload-progress-container">
              <ProgressBar
                showValueLabel
                aria-label={t("Upload progress")}
                className="w-full"
                data-id="upload-progress-bar"
                value={uploadProgress}
              />
            </div>
          )}

          {/* File Upload Error */}
          {errorState.hasError && errorState.error && (
            <div className="px-4 pb-2 space-y-2">
              <FileUploadError
                data-testid="upload-error-message"
                error={errorState.error}
                showRemove={true}
                showRetry={!!retryUpload}
                onRemove={clearError}
                onRetry={retryUpload || undefined}
              />

              {failedFiles.length > 1 && (
                <div className="flex justify-center">
                  <button
                    className="text-sm text-danger hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
                    data-testid="retry-all-failed-button"
                    disabled={isUploading}
                    type="button"
                    onClick={retryFailedUploads}
                  >
                    {t("fileUpload.retryAllFailed", {
                      defaultValue: `Retry All Failed (${failedFiles.length})`,
                      count: failedFiles.length,
                    })}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* File List */}
          {uploadedFiles.length > 0 && (
            <div
              className="flex flex-wrap gap-2 max-h-24 overflow-y-auto px-4 pb-2"
              data-id="uploaded-files-container"
            >
              {uploadedFiles.map((file) => {
                const isFileUploading = file.status === "uploading" || file.status === "processing";
                const fileStatusText = file.status ? fileUploadStatusLabel(file.status, t) : "";

                return (
                  <Tooltip key={file.id}>
                    <TooltipTrigger>
                      <button
                        aria-label={t("fileUpload.removeFile")}
                        className="inline-flex items-center gap-1.5 rounded-full border border-default-300 bg-default-100 px-2 py-1 text-xs hover:bg-default-200 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        data-id={`file-${file.id}`}
                        disabled={isFileUploading}
                        type="button"
                        onClick={() => removeFile(file.name)}
                      >
                        <FileTypeIcon icon={convertFileTypeToIcon(file.type)} width={16} />
                        <span data-id={`upload-file-name-${file.name}`}>{file.name}</span>
                        {fileStatusText && (
                          <span className="ml-2 text-xs text-default-500">({fileStatusText})</span>
                        )}
                        <Icon
                          aria-hidden="true"
                          className={isFileUploading ? "text-default-400" : "hover:text-danger"}
                          data-id={`remove-file-${file.id}`}
                          icon="solar:close-circle-linear"
                          width={16}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent showArrow>
                      {isFileUploading
                        ? `${fileStatusText}...`
                        : t("Remove {{title}}", { title: file.name })}
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          )}
        </form>

        {/* Helper line below the input form */}
        <p
          className="px-2 text-[11px] text-default-400 select-none"
          data-id="chat-input-helper-line"
        >
          {t("chatInput.helperLine")}
        </p>
      </div>
      <PromptSearchModal
        data-id="prompt-generator-modal"
        isOpen={isTemplateModalOpen}
        onOpenChange={setIsTemplateModalOpen}
        onSelectTemplate={handleTemplateSelect}
      />
      <ChatSettingsModal
        data-id="chat-settings-modal"
        isOpen={isSettingsModalOpen}
        onOpenChange={setIsSettingsModalOpen}
      />
      <ConfirmationDialog isOpen={isConfirmDialogOpen} {...confirmationProps} />

      {/* Prompto active indicator (subtle, replaces the old inline active state) */}
      {isOnPromptoWorkflow && (
        <div className="sr-only" role="status">
          {t("Prompto active")}
        </div>
      )}
    </div>
  );
};
